import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import QRCode from 'qrcode';
import { mfaDisableSchema, mfaEnrollConfirmSchema, type MfaDisableInput, type MfaEnrollConfirmInput } from '@orion/contracts';
import { Button, Card, Input } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { ApiError } from '../../lib/api-client.js';
import { useMfaDisable, useMfaEnrollConfirm, useMfaEnrollStart, useMfaStatus } from './api.js';

type ViewState = 'status' | 'enrolling' | 'recoveryCodes' | 'disabling';

/** docs/architecture/05-authentication.md § MFA — a self-service page since
 * enrollment is per-user, not tenant-wide (that's Admin ▸ Security's
 * `mfaRequiredFor` policy, a separate screen). */
export function AccountSecurityPage() {
  const { data: status, isLoading } = useMfaStatus();
  const [view, setView] = useState<ViewState>('status');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  useEffect(() => {
    if (status && !status.enabled) setView('status');
  }, [status]);

  return (
    <>
      <PageHeader title="Security" />
      <main className="mx-auto flex max-w-[560px] flex-col gap-5 p-7">
        <Card className="p-6">
          <h2 className="text-[15px] font-semibold text-ink">Two-factor authentication</h2>
          <p className="mt-1 text-sm text-text-muted">
            Require a code from an authenticator app in addition to your password when you sign in.
          </p>

          {isLoading ? (
            <p className="mt-4 text-sm text-text-muted">Loading…</p>
          ) : view === 'status' ? (
            <div className="mt-4 flex items-center justify-between rounded-control bg-ground px-4 py-3">
              <span className="text-sm font-medium text-ink">
                {status?.enabled ? 'Enabled' : 'Not enabled'}
              </span>
              {status?.enabled ? (
                <Button variant="secondary" onClick={() => setView('disabling')}>
                  Disable
                </Button>
              ) : (
                <Button onClick={() => setView('enrolling')}>Enable</Button>
              )}
            </div>
          ) : null}

          {view === 'enrolling' && (
            <EnrollFlow
              onCancel={() => setView('status')}
              onEnrolled={(codes) => {
                setRecoveryCodes(codes);
                setView('recoveryCodes');
              }}
            />
          )}

          {view === 'recoveryCodes' && (
            <RecoveryCodes codes={recoveryCodes} onDone={() => setView('status')} />
          )}

          {view === 'disabling' && <DisableFlow onCancel={() => setView('status')} onDisabled={() => setView('status')} />}
        </Card>
      </main>
    </>
  );
}

function EnrollFlow({ onCancel, onEnrolled }: { onCancel: () => void; onEnrolled: (recoveryCodes: string[]) => void }) {
  const enrollStart = useMfaEnrollStart();
  const enrollConfirm = useMfaEnrollConfirm();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MfaEnrollConfirmInput>({ resolver: zodResolver(mfaEnrollConfirmSchema) });

  const { mutateAsync: startEnroll } = enrollStart;
  useEffect(() => {
    startEnroll().then(async (result) => {
      setSecret(result.secret);
      setQrDataUrl(await QRCode.toDataURL(result.otpauthUrl));
    });
  }, [startEnroll]);

  const errorMessage = enrollConfirm.error instanceof ApiError ? (enrollConfirm.error.problem.detail ?? enrollConfirm.error.problem.title) : undefined;

  const onSubmit = async (input: MfaEnrollConfirmInput) => {
    const result = await enrollConfirm.mutateAsync(input);
    onEnrolled(result.recoveryCodes);
  };

  return (
    <div className="mt-4 flex flex-col gap-4 border-t border-border pt-4">
      <p className="text-sm text-text-secondary">Scan this QR code with your authenticator app (e.g. Google Authenticator, 1Password, Authy).</p>
      {qrDataUrl ? (
        <img src={qrDataUrl} alt="Scan with your authenticator app" className="size-40 self-center rounded-control border border-border" />
      ) : (
        <div className="h-40 w-40 self-center animate-pulse rounded-control bg-ground" />
      )}
      {secret && (
        <p className="self-center text-center text-xs text-text-muted">
          Can't scan it? Enter this code manually: <span className="font-mono font-semibold text-ink">{secret}</span>
        </p>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-3">
        <Input label="Enter the 6-digit code" inputMode="numeric" error={errors.code?.message} {...register('code')} />
        {errorMessage && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {errorMessage}
          </div>
        )}
        <div className="flex gap-2">
          <Button type="submit" loading={isSubmitting}>
            Confirm & enable
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="mt-4 flex flex-col gap-4 border-t border-border pt-4">
      <p className="text-sm font-semibold text-ink">Save your recovery codes</p>
      <p className="text-sm text-text-muted">
        Each code can be used once to sign in if you lose access to your authenticator app. Store them somewhere safe — they won't be shown again.
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-control bg-ground p-4 font-mono text-sm text-ink">
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
      <Button onClick={onDone} className="self-start">
        I've saved these codes
      </Button>
    </div>
  );
}

function DisableFlow({ onCancel, onDisabled }: { onCancel: () => void; onDisabled: () => void }) {
  const disable = useMfaDisable();
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MfaDisableInput>({ resolver: zodResolver(mfaDisableSchema) });

  const errorMessage = disable.error instanceof ApiError ? (disable.error.problem.detail ?? disable.error.problem.title) : undefined;

  const onSubmit = async (input: MfaDisableInput) => {
    await disable.mutateAsync(input);
    onDisabled();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
      <p className="text-sm text-text-secondary">Confirm with a current code to turn off two-factor authentication.</p>
      {useRecoveryCode ? (
        <Input label="Recovery code" error={errors.recoveryCode?.message} {...register('recoveryCode')} />
      ) : (
        <Input label="Authentication code" inputMode="numeric" error={errors.code?.message} {...register('code')} />
      )}
      {errorMessage && (
        <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
          {errorMessage}
        </div>
      )}
      <div className="flex gap-2">
        <Button type="submit" variant="danger" loading={isSubmitting}>
          Disable
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <button type="button" onClick={() => setUseRecoveryCode((v) => !v)} className="self-start text-xs font-medium text-text-muted hover:text-ink">
        {useRecoveryCode ? 'Use my authenticator app instead' : 'Use a recovery code instead'}
      </button>
    </form>
  );
}
